import React, { useState } from 'react';
import ShellThemePicker from '../../../shared/components/common/ShellThemePicker';
import SidebarModePicker from '../../../shared/components/common/SidebarModePicker';
import FilterDropdown from '../../../shared/components/common/FilterDropdown';
import {
  LIST_PAGE_SIZES,
  ALERT_VIEW_MODES,
  getListPageSize,
  getAlertsViewMode,
  getLandingView,
  getLoginAlertsToast,
  getComplaintsStatusFilter,
  getDevicesFilter,
  getComplaintsSort,
  getDevicesSort,
  saveUiPreferencePatch,
} from '../../../shared/utils/uiPreferences';
import {
  getLandingViewsForRole,
  resolveLandingView,
} from '../../../shared/utils/landingView';
import {
  COMPLAINTS_STATUS_FILTER_OPTIONS,
  DEVICES_FILTER_OPTIONS,
  COMPLAINTS_SORT_OPTIONS,
  DEVICES_SORT_OPTIONS,
} from '../../../shared/utils/listDefaults';
import './PreferencesPage.css';

/**
 * User preferences — category cards for clear scanning.
 * Role-specific cards can be inserted into the grid later.
 */
function PreferencesPage({ user }) {
  const landingOptions = getLandingViewsForRole(user?.role);
  const [listPageSize, setListPageSize] = useState(getListPageSize);
  const [alertsViewMode, setAlertsViewMode] = useState(getAlertsViewMode);
  const [loginAlertsToast, setLoginAlertsToast] = useState(getLoginAlertsToast);
  const [landingView, setLandingView] = useState(() =>
    resolveLandingView(getLandingView(), user?.role)
  );
  const [complaintsStatusFilter, setComplaintsStatusFilter] = useState(getComplaintsStatusFilter);
  const [devicesFilter, setDevicesFilter] = useState(getDevicesFilter);
  const [complaintsSort, setComplaintsSort] = useState(getComplaintsSort);
  const [devicesSort, setDevicesSort] = useState(getDevicesSort);

  const listPageSizeOptions = LIST_PAGE_SIZES.map((n) => ({ id: n, label: String(n) }));

  const onListPageSizeChange = (value) => {
    const size = parseInt(value, 10);
    setListPageSize(size);
    saveUiPreferencePatch({ list_page_size: size });
  };

  const onAlertsViewModeChange = (value) => {
    setAlertsViewMode(value);
    saveUiPreferencePatch({ alerts_view_mode: value });
  };

  const onLoginAlertsToastChange = (enabled) => {
    setLoginAlertsToast(enabled);
    saveUiPreferencePatch({ login_alerts_toast: enabled });
  };

  const onLandingViewChange = (value) => {
    setLandingView(value);
    saveUiPreferencePatch({ landing_view: value });
  };

  const onComplaintsStatusFilterChange = (value) => {
    setComplaintsStatusFilter(value);
    saveUiPreferencePatch({ complaints_status_filter: value });
  };

  const onDevicesFilterChange = (value) => {
    setDevicesFilter(value);
    saveUiPreferencePatch({ devices_filter: value });
  };

  const onComplaintsSortChange = (value) => {
    setComplaintsSort(value);
    saveUiPreferencePatch({ complaints_sort: value });
  };

  const onDevicesSortChange = (value) => {
    setDevicesSort(value);
    saveUiPreferencePatch({ devices_sort: value });
  };

  return (
    <div className="preferences-page">
      <header className="preferences-header">
        <h2>🧩 Preferences</h2>
        <p className="preferences-subtitle">
          Changes save to your account and apply on any browser when you sign in.
        </p>
      </header>

      <div className="preferences-grid">
        {/* Appearance — full width (swatches need horizontal room) */}
        <article className="pref-category pref-category--wide">
          <div className="pref-category-header">
            <span className="pref-category-icon" aria-hidden="true">🎨</span>
            <div>
              <h3 className="pref-category-title">Appearance</h3>
              <p className="pref-category-desc">
                Default navigation (nav bar, icons only, or hamburger) and page backdrop.
                Chrome and accent colors are set by your organization superadmin under Organization in the sidebar.
              </p>
            </div>
          </div>
          <div className="pref-category-body">
            <div className="pref-appearance-block">
              <SidebarModePicker layout="page" />
            </div>
            <div className="pref-appearance-block">
              <ShellThemePicker layout="page" />
            </div>
          </div>
        </article>

        {/* Home / landing page */}
        <article className="pref-category">
          <div className="pref-category-header">
            <span className="pref-category-icon" aria-hidden="true">🏠</span>
            <div>
              <h3 className="pref-category-title">Home</h3>
              <p className="pref-category-desc">
                Page that opens after you sign in.
              </p>
            </div>
          </div>
          <div className="pref-category-body">
            <div className="pref-control">
              <span className="pref-control-label" id="pref-landing-view-label">
                Landing page
              </span>
              <FilterDropdown
                id="pref-landing-view"
                className="pref-filter-dropdown"
                options={landingOptions}
                value={landingView}
                onChange={onLandingViewChange}
                title="Page that opens after you sign in"
              />
            </div>
          </div>
        </article>

        {/* Lists */}
        <article className="pref-category">
          <div className="pref-category-header">
            <span className="pref-category-icon" aria-hidden="true">📋</span>
            <div>
              <h3 className="pref-category-title">Lists</h3>
              <p className="pref-category-desc">
                Default number of rows per page on list views across the app.
              </p>
            </div>
          </div>
          <div className="pref-category-body">
            <div className="pref-control">
              <span className="pref-control-label" id="pref-list-page-size-label">
                Rows per page
              </span>
              <FilterDropdown
                id="pref-list-page-size"
                className="pref-filter-dropdown"
                options={listPageSizeOptions}
                value={listPageSize}
                onChange={onListPageSizeChange}
                title="Default rows per page"
              />
            </div>
          </div>
        </article>

        {/* Complaints defaults */}
        <article className="pref-category">
          <div className="pref-category-header">
            <span className="pref-category-icon" aria-hidden="true">🛠️</span>
            <div>
              <h3 className="pref-category-title">Complaints</h3>
              <p className="pref-category-desc">
                Default status filter and sort on the Complaints list page only — not dashboards or other views.
              </p>
            </div>
          </div>
          <div className="pref-category-body">
            <div className="pref-control">
              <span className="pref-control-label">Default status</span>
              <FilterDropdown
                className="pref-filter-dropdown"
                options={COMPLAINTS_STATUS_FILTER_OPTIONS}
                value={complaintsStatusFilter}
                onChange={onComplaintsStatusFilterChange}
                title="Default status on the Complaints list"
              />
            </div>
            <div className="pref-control">
              <span className="pref-control-label">Default sort</span>
              <FilterDropdown
                className="pref-filter-dropdown"
                options={COMPLAINTS_SORT_OPTIONS}
                value={complaintsSort}
                onChange={onComplaintsSortChange}
                title="Default sort on the Complaints list"
              />
            </div>
          </div>
        </article>

        {/* Devices defaults */}
        <article className="pref-category">
          <div className="pref-category-header">
            <span className="pref-category-icon" aria-hidden="true">📟</span>
            <div>
              <h3 className="pref-category-title">Devices</h3>
              <p className="pref-category-desc">
                Default filter and sort on the Devices list page only — not dashboards or other views.
              </p>
            </div>
          </div>
          <div className="pref-category-body">
            <div className="pref-control">
              <span className="pref-control-label">Default filter</span>
              <FilterDropdown
                className="pref-filter-dropdown"
                options={DEVICES_FILTER_OPTIONS}
                value={devicesFilter}
                onChange={onDevicesFilterChange}
                title="Default filter on the Devices list"
              />
            </div>
            <div className="pref-control">
              <span className="pref-control-label">Default sort</span>
              <FilterDropdown
                className="pref-filter-dropdown"
                options={DEVICES_SORT_OPTIONS}
                value={devicesSort}
                onChange={onDevicesSortChange}
                title="Default sort on the Devices list"
              />
            </div>
          </div>
        </article>

        {/* Alerts */}
        <article className="pref-category">
          <div className="pref-category-header">
            <span className="pref-category-icon" aria-hidden="true">🔔</span>
            <div>
              <h3 className="pref-category-title">Alerts</h3>
              <p className="pref-category-desc">
                Default Alerts tab and optional login summary. The header badge always shows the count.
              </p>
            </div>
          </div>
          <div className="pref-category-body">
            <div className="pref-control">
              <span className="pref-control-label">Default view</span>
              <FilterDropdown
                className="pref-filter-dropdown"
                options={ALERT_VIEW_MODES}
                value={alertsViewMode}
                onChange={onAlertsViewModeChange}
                title="Default Alerts tab"
              />
            </div>
            <div className="pref-control pref-control--toggle">
              <div className="pref-toggle-copy">
                <span className="pref-control-label" id="pref-login-alerts-toast-label">
                  Show alerts summary on login
                </span>
                <p className="pref-control-hint">
                  One toast per session when alerts exist (off by default).
                </p>
              </div>
              <label className="pref-toggle">
                <input
                  type="checkbox"
                  checked={loginAlertsToast}
                  onChange={(e) => onLoginAlertsToastChange(e.target.checked)}
                  aria-labelledby="pref-login-alerts-toast-label"
                />
                <span className="pref-toggle-track" aria-hidden="true" />
              </label>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
}

export default PreferencesPage;
